import './index.scss'

import { useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import VolunteeringController from '@data/controller/VolunteeringController'
import VolunteerTeam from '@data/interfaces/Volunteering/volunteerteam.interface'

import closeIcon from 'url:@icon/close.svg';

// The caller keys this per open, so the field always starts from the team's
// current name.
export default function EditTeamModal(props: {
  isOpen: boolean,
  team?: VolunteerTeam | null,
  close: () => void,
  onSaved: () => void
}) {
  const auth = useAuth();
  const currentName = props.team?.attributes.name ?? '';

  const [name, setName] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  // The API rejects an empty name, and saving an unchanged one is a no-op.
  const canSave = trimmedName.length > 0 && trimmedName !== currentName.trim() && !isSaving;

  const save = async () => {
    if (!auth.churchId || !props.team) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    if (!canSave) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = await VolunteeringController.updateTeam(auth.churchId, props.team.id, trimmedName, auth);

      if (response.error) {
        setError(response.error);
        return;
      }

      // The page refetches for the saved name rather than reading it back from
      // this response, which may carry it in its stored, localized form.
      props.onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='edit-team-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='edit-team-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isSaving}
          onClick={props.close}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>Edit Team</h3>

        <span className='header-control-spacer' />
      </div>

      <form
        className='body'
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}>
        {error ? <p className='save-error'>{error}</p> : null}

        <label className='field-label' htmlFor='edit-team-name'>Team name</label>
        <input
          id='edit-team-name'
          type='text'
          value={name}
          autoFocus
          disabled={isSaving}
          onChange={(e) => setName(e.target.value)}
        />

        <button type='submit' className='save-button' disabled={!canSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  </Modal>
}
